/**
 * Widget Index Mapping Utilities
 * -------------------------------------
 * Maps a widget's visible position (filtered UI list) to its actual index
 * in the Redux store's full widget list.
 *
 * Used for add, delete, and update operations that require
 * mapping between UI and data layers.
 */

/**
 * Gets the widget at visibleIndex and finds its exact index in the Redux store widgets array
 * @param visibleIndex - Index in the displayed widgets array (UI layer)
 * @param visibleWidgets - The filtered widgets currently visible in the UI (no footers)
 * @param storeWidgets - The full widgets array from the Redux store
 * @returns The matching index in the Redux store, or -1 if not found
 *
 * @example
 * const storeIndex = getWidgetIndexForOperation(1, visibleWidgets, storeWidgets);
 * if (storeIndex !== -1) dispatch(deleteWidget(storeIndex));
 */
export function getWidgetIndexForOperation(
  visibleIndex: number,
  visibleWidgets: any[],
  storeWidgets: any[]
): number {
  // Validate input parameters
  if (!Array.isArray(visibleWidgets) || !Array.isArray(storeWidgets)) {
    return -1;
  }

  if (visibleIndex < 0 || visibleIndex >= visibleWidgets.length) {
    return -1;
  }

  // Get the target widget from visibleWidgets at the specified index
  const targetWidget = visibleWidgets[visibleIndex];

  if (!targetWidget?.type) {
    return -1;
  }

  // Find the exact widget object in the storeWidgets array
  const storeIndex = storeWidgets.findIndex((widget: any) => widget === targetWidget);

  return storeIndex;
}
